import { Box, Card, CardBody, CardHeader, Divider, Heading, Image, Stack, Text } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { CargoType, DocumentType } from '@pelicargo/types';
import { buildFlightPathString, Show } from '@pelicargo/ui';
import { prettyDate, prettyDockHour } from '@pelicargo/utils';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import z from 'zod';

import { CopyButton } from '../../components/CopyButton';
import { CopyLineItemsButton } from '../../components/CopyLineItemsButton';
import { DocumentsForm } from '../../components/Documents/DocumentsForm ';
import { InstructionsCard } from '../../components/InstructionCard';
import { PickupSection } from '../../components/PickupSection';
import { QuoteVariantUI } from '../../components/QuoteVariantTabs/QuoteVariant';
import { RequestDetailsCard } from '../../components/RequestDetailsCard';
import { SimpleForm } from '../../components/SimpleForm';
import { Timeline } from '../../components/Timeline';
import { Validity } from '../../components/Validity';
import { TrpcOutputs } from '../../config/trpc';
import { useFlightData } from '../../hooks/useFlightData';

type Props = {
  quote: TrpcOutputs['getFinalQuote']['quote'];
};

export const ExwBookingConfirmation = ({ quote }: Props) => {
  const { flightData } = useFlightData(quote?.flight_path);

  const bookedQuoteVariant = quote?.request?.booked_quote_variant;
  const inlandQuote = bookedQuoteVariant?.inland_service_quote_variant?.quote;

  const schema = z.object({
    documents: z
      .array(
        z.object({
          id: z.number(),
          created_at: z.coerce.date().nullish(),
          updated_at: z.coerce.date().nullish(),
          original_filename: z.string(),
          type: z.nativeEnum(DocumentType, { invalid_type_error: 'Invalid Document Type' }).nullish(),
          request_id: z.number().nullish(),
          document_id: z.number().nullish(),
        }),
      )
      .optional(),
  });
  const request = quote?.request;
  const methods = useForm<{
    documents: Array<{
      id: number;
      created_at: Date | null;
      updated_at: Date | null;
      original_filename: string;
      type: DocumentType | null;
      request_id: number | null;
      document_id: number | null;
    }>;
  }>({
    mode: 'onBlur',
    resolver: zodResolver(schema),
    defaultValues: {
      documents: request?.documents || [],
    },
  });

  const isDgd = useMemo(() => request?.cargo_type === CargoType.DangerousGoods, [request?.cargo_type]);

  const documentTypes = useMemo(() => {
    const suggested: DocumentType[] = [DocumentType.SLI, DocumentType.SHIPPER_DOCUMENTS, DocumentType.AIRWAY_BILL];
    if (isDgd) suggested.push(DocumentType.DGD, DocumentType.MSDS);

    return suggested;
  }, [isDgd]);

  const stopCountLabel = useMemo(() => {
    const numStops = quote?.flight_path?.length - 1;

    if (numStops === 0) return 'Nonstop';
    if (numStops === 1) return '1 stop';
    return `${numStops} stops`;
  }, [quote?.flight_path?.length]);

  useEffect(() => {
    methods.reset({
      ...methods.getValues(),
      documents: request?.documents || [],
    });
  }, [request?.documents, methods]);

  return (
    <Stack direction={{ base: 'column', lg: 'row' }} spacing="6">
      <Stack w="full" spacing="6">
        <Show if={!!quote?.booking_instructions}>
          <InstructionsCard instructions={quote?.booking_instructions} />
        </Show>
        <SimpleForm onSubmit={() => {}} {...(methods as any)}>
          <Card h="fit-content">
            <CardHeader py="3">
              <Heading variant="h5">Documents</Heading>
            </CardHeader>
            <CardBody pt="0">
              <DocumentsForm
                name="documents"
                requestId={quote?.request?.id}
                documentTypes={documentTypes}
                isApprovable
              />
            </CardBody>
          </Card>
        </SimpleForm>
        <Card>
          <CardHeader pl="4" pr="4" pt="3" pb="3">
            <Stack>
              <Stack direction="row" align="center" spacing="6">
                <Image src={quote?.provider?.logo_uri} height="48px" width="48px" alt={quote?.provider?.name} />
                <Heading variant="h6">
                  {quote?.origin_airport_id}{' '}
                  <Box as="span" color="gray.400">
                    →
                  </Box>{' '}
                  {quote?.destination_airport_id}
                </Heading>
                <Text textStyle="bodySmallRegular">{stopCountLabel}</Text>
                <CopyButton text={buildFlightPathString(quote?.flight_path)} marginLeft="auto" />
              </Stack>
              <Text color="text.subtle" textStyle="captionSmallRegular">
                {quote?.provider?.name}
              </Text>
            </Stack>
          </CardHeader>
          <Stack>
            <Divider orientation="horizontal" />
          </Stack>
          <CardBody>
            <Timeline data={flightData} />
          </CardBody>
        </Card>
        <Card borderRadius="lg" role="group">
          <CardHeader py="3">
            <Heading variant="h5">Pick Up</Heading>
          </CardHeader>
          <CardBody pt="0">
            <PickupSection
              readyDate={prettyDate(inlandQuote?.pickup_ready_at, { timeZone: 'Etc/UTC' })}
              dockHours={`${prettyDockHour(inlandQuote?.from_dock_hours)} - ${prettyDockHour(inlandQuote?.to_dock_hours)}`}
              address={inlandQuote?.delivery_address}
              pickupContact={inlandQuote?.inland_dropoff_contact || {}}
              truckingRef={inlandQuote?.trucking_ref}
              truckCompany={inlandQuote?.provider?.name}
              estimatedPickup={
                inlandQuote?.estimated_pickup_at
                  ? prettyDate(inlandQuote.estimated_pickup_at, { showTime: true, timeZone: 'Etc/UTC' })
                  : undefined
              }
              flex="1"
            />
          </CardBody>
        </Card>
      </Stack>
      <Stack w="full" spacing="6" alignItems="flex-start">
        <Card h="fit-content">
          <CardBody p="4">
            <Stack spacing={6}>
              <Stack direction="row" align="center" justify="space-between" spacing="4" role="group">
                <Heading variant="h5">Cost Summary</Heading>
                <CopyLineItemsButton quote={quote} quoteVariant={bookedQuoteVariant} />
              </Stack>
              <QuoteVariantUI quote={quote} quoteVariant={bookedQuoteVariant} showCopy={false} />
              <Validity quote={quote} />
            </Stack>
          </CardBody>
        </Card>
        <RequestDetailsCard request={quote?.request} quote={quote} />
      </Stack>
    </Stack>
  );
};
